import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Col, Dropdown, Row } from 'react-bootstrap';
import {
  ArrowDownUp,
  ArrowLeft,
  Calculator,
  Calendar,
  Cash,
  ChatLeftText,
  ClipboardCheck,
  Copy,
  FileEarmarkText,
  Files,
  GraphUp,
  JournalText,
  Pencil,
  Plus,
  Receipt,
  Telephone,
  Trash
} from 'react-bootstrap-icons';
import { toast } from 'react-toastify';
import { deleteFileFromServer, makeApiRequests } from '../../helpers/api';
import { ENDPOINTS } from '../../helpers/constants';
import { calculateCommissionDue, copyContactAddress, copyContactDetails, openLinkInNewTab } from '../../helpers/global';
import { getModelLabel } from '../../helpers/formHelpers';
import { choiceValues, lastContactLabel } from './helpers';
import { getProfileFromLocalStorage } from '../../helpers/session';
import { createTaskUpdatePayload, validateTasks } from '../../helpers/taskHelpers';
import AlertModal from '../AlertModal';
import PnLSidebar from '../PnlSidebar';
import FileViewer from '../common/FileViewer';
import UnderlineButton from '../common/UnderlineButton';
import InventoryPicker from '../inventory/InventoryPicker';
import CalculationWorksheetSidebar from '../pipeline/CalculationWorksheetSidebar';
import DochubSidebar from '../pipeline/DochubSidebar';
import { InventoryEditSideBar } from '../pipeline/Pipeline';
import PayrollViewSidebar from '../reports/payroll/PayrollViewSidebar';
import Tasks from '../tasks/Tasks';

const propertyAddress = record =>
  [record?.propertyAddress, record?.propertyCity, record?.propertyState, record?.zipCode]
    .map(part => String(part || '').trim())
    .filter(Boolean)
    .join(', ');

const money = value => Number(value || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

const dealTools = [
  [GraphUp, 'P&L', 'warning'],
  [Receipt, 'Payroll', 'warning'],
  [Cash, 'Comm. Sheet', 'info'],
  [ClipboardCheck, 'File Checklist', 'info'],
  [FileEarmarkText, 'Dochub', 'warning'],
  [Files, 'Files', 'dark'],
  [Calculator, 'Calculation', 'info'],
  [Copy, 'Copy', 'warning']
];

const inventoryUnits = contact => {
  const units = contact?.inventoryUnit;
  if (Array.isArray(units)) return units;
  return units ? [units] : [];
};

const salesLocationLabel = location => {
  if (!location || typeof location !== 'object') return '';
  return String(location.location || '').trim();
};

function CustomerDrawer({
  lead,
  appChoices,
  onClose,
  onNote,
  onComplete,
  onReschedule,
  onOpenRecord,
  onTasksSaved,
  onStatusChange,
  showNextAction = true
}) {
  const [contact, setContact] = useState(null);
  const [dochubDoc, setDochubDoc] = useState(null);
  const [showFiles, setShowFiles] = useState(false);
  const [showPnl, setShowPnl] = useState(false);
  const [showCalc, setShowCalc] = useState(false);
  const [calcUnit, setCalcUnit] = useState(null);
  const [deleteFile, setDeleteFile] = useState(null);
  const [showPayroll, setShowPayroll] = useState(false);
  const [payrolls, setPayrolls] = useState([]);
  const [payrollLoading, setPayrollLoading] = useState(false);
  const [payrollBillMeta, setPayrollBillMeta] = useState(null);
  const [showRecordPayroll, setShowRecordPayroll] = useState(false);
  const [toBeDeletedBill, setToBeDeletedBill] = useState(null);
  const [deleteInProgress, setDeleteInProgress] = useState(false);
  const [copyOpen, setCopyOpen] = useState(false);
  const [showTasks, setShowTasks] = useState(false);
  const [statusSaving, setStatusSaving] = useState(false);
  const [inventoryPopup, setInventoryPopup] = useState(null);
  const [editingInventory, setEditingInventory] = useState(null);
  const [removeUnitId, setRemoveUnitId] = useState(null);
  const [savingUnits, setSavingUnits] = useState(false);
  const [unitFileViewer, setUnitFileViewer] = useState(null);
  const statusTouched = useRef(false);
  const currentUserId = getProfileFromLocalStorage()?._id;

  useEffect(() => {
    let active = true;
    makeApiRequests({
      endpoint: ENDPOINTS.CONTACTS_SEARCH,
      requestBody: { filter: { _id: lead._id } }
    }).then(({ response }) => {
      if (!active || !Array.isArray(response) || !response[0]) return;
      setContact(current =>
        statusTouched.current && current?.status ? { ...response[0], status: current.status } : response[0]
      );
    });
    return () => {
      active = false;
    };
  }, [lead._id]);

  const currentStatus = contact?.status || lead.status || '';
  const statusOptions = useMemo(() => {
    const values = choiceValues(appChoices || [], 'Contact Status');
    if (currentStatus && !values.includes(currentStatus)) return [currentStatus, ...values];
    return values.length ? values : currentStatus ? [currentStatus] : [];
  }, [appChoices, currentStatus]);
  const subtitle = [
    salesLocationLabel(contact?.salesLocation),
    contact?.propertyCity || contact?.salesLocation?.city,
    contact?.contactOwner?.name
  ]
    .filter(Boolean)
    .join(' · ');
  const phone = contact?.cellPhone || lead.cellPhone || '';
  const lastContact = lead.lastContactLabel || lastContactLabel(contact) || '…';
  const actionLabel =
    lead.bucket === 'overdue' ? 'Next action · Overdue' : lead.bucket === 'due' ? 'Next action · Due today' : 'Next action';
  const units = inventoryUnits(contact);
  const contract =
    contact?.exportedContract && typeof contact.exportedContract === 'object' ? contact.exportedContract : null;
  const address = propertyAddress(contact) || propertyAddress(contract);
  const contractUnit =
    contract?.inventoryUnit &&
    typeof contract.inventoryUnit === 'object' &&
    (contract.inventoryUnit.serialA ||
      contract.inventoryUnit.invoice != null ||
      contract.inventoryUnit.commissionSheetOptions)
      ? contract.inventoryUnit
      : null;
  const commissionUnit = contractUnit || units[0];
  const commissionSource = contract?.salesPrice != null && contract.salesPrice !== '' ? contract : contact;
  const commissionDue =
    contact && commissionUnit && commissionSource?.salesPrice != null && commissionSource.salesPrice !== ''
      ? calculateCommissionDue({ ...commissionSource, inventoryUnit: commissionUnit })
      : null;
  const toolOpen = Boolean(dochubDoc || showFiles || showPnl || showCalc || showPayroll || unitFileViewer);

  const submitTasks = async updatedTasks => {
    const tasksWithData = updatedTasks.filter(task => task.description?.trim() || task.assignedTo || task.dueDate);
    if (tasksWithData.length > 0) {
      try {
        validateTasks(tasksWithData);
      } catch (error) {
        toast.error(error.message);
        return;
      }
    }
    const { response, error } = await makeApiRequests({
      endpoint: ENDPOINTS.CONTACTS_WITH_ID(contact._id),
      method: 'PUT',
      requestBody: createTaskUpdatePayload(updatedTasks, currentUserId)
    });
    if (error) {
      toast.error(error);
      return;
    }
    setContact(current => ({ ...current, tasks: response?.tasks || [] }));
    toast.success('Tasks saved successfully!');
    if (onTasksSaved) onTasksSaved();
  };

  const changeStatus = async next => {
    if (!next || next === currentStatus || statusSaving) return;
    statusTouched.current = true;
    setStatusSaving(true);
    const { response, error } = await makeApiRequests({
      endpoint: ENDPOINTS.CONTACTS_WITH_ID(lead._id),
      method: 'PUT',
      requestBody: { status: next }
    });
    setStatusSaving(false);
    if (error) {
      toast.error(error);
      return;
    }
    if (response) setContact(response);
    else setContact(current => (current ? { ...current, status: next } : current));
    if (onStatusChange) onStatusChange(response?.status || next);
    toast.success(`${lead.buyer} moved to ${response?.status || next}.`);
  };

  const ready = () => {
    if (contact) return true;
    toast.error('Still loading this customer.');
    return false;
  };

  const openPayroll = async () => {
    if (!ready()) return;
    if (!contract?._id) {
      toast.error('This customer is not on a contract yet.');
      return;
    }
    setShowPayroll(true);
    setPayrollLoading(true);
    const { response, error } = await makeApiRequests({
      endpoint: ENDPOINTS.PAYROLLS_SEARCH,
      requestBody: { sortBy: '-date', filter: { contract: contract._id } }
    });
    setPayrollLoading(false);
    if (error) {
      toast.error(error);
      return;
    }
    setPayrolls(
      [...(response || [])].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
    );
  };

  const updatePayroll = newPayroll => {
    setPayrolls(current => {
      const next = [...current];
      const index = next.findIndex(item => item._id === newPayroll._id);
      if (index === -1) next.push(newPayroll);
      else next[index] = newPayroll;
      return next;
    });
  };

  const submitPayroll = async (payroll, files) => {
    const payrollToBeUpdated = payrollBillMeta?.payrollBill;
    if (!payrollToBeUpdated) return;
    const newPayroll = {
      ...payroll,
      files: [...(payrollToBeUpdated.files || []).map(file => file._id), ...files]
    };
    setPayrollBillMeta({ ...payrollBillMeta, submittingPayroll: true });
    const { response, error } = await makeApiRequests({
      requestBody: newPayroll,
      endpoint: ENDPOINTS.PAYROLLS_WITH_ID(payrollToBeUpdated._id),
      method: 'PUT'
    });
    setPayrollBillMeta(current => ({ ...current, submittingPayroll: false }));
    if (error) {
      toast.error(error);
      return;
    }
    toast.success('Payroll bill updated successfully...');
    setPayrollBillMeta(null);
    updatePayroll(response);
  };

  const deletePayroll = async () => {
    setDeleteInProgress(true);
    const { error } = await makeApiRequests({
      endpoint: ENDPOINTS.PAYROLLS_WITH_ID(toBeDeletedBill._id),
      method: 'DELETE'
    });
    setDeleteInProgress(false);
    if (error) {
      toast.error(error);
      return;
    }
    setPayrolls(current => current.filter(item => item._id !== toBeDeletedBill._id));
    setToBeDeletedBill(null);
  };

  const unitIds = units.map(unit => unit?._id).filter(Boolean);

  const saveContactUnits = async nextIds => {
    if (!contact?._id) return;
    setSavingUnits(true);
    const { response, error } = await makeApiRequests({
      endpoint: ENDPOINTS.CONTACTS_WITH_ID(contact._id),
      requestBody: { inventoryUnit: nextIds },
      method: 'PUT'
    });
    setSavingUnits(false);
    if (error) {
      toast.error(error);
      return;
    }
    toast.success('Updated Successfully');
    setContact(response);
    setInventoryPopup(null);
    setRemoveUnitId(null);
  };

  const onInventorySubmit = async inventoryList => {
    const newUnit = inventoryList?.[0];
    if (!newUnit?._id) {
      setInventoryPopup(null);
      return;
    }
    const nextIds = inventoryPopup?.replaceId
      ? unitIds.map(id => (id === inventoryPopup.replaceId ? newUnit._id : id))
      : [...unitIds, newUnit._id];
    await saveContactUnits(nextIds);
  };

  const onUpdateInventory = updatedInventory => {
    setContact(current =>
      current
        ? {
            ...current,
            inventoryUnit: inventoryUnits(current).map(unit =>
              unit?._id === updatedInventory?._id ? updatedInventory : unit
            )
          }
        : current
    );
    if (unitFileViewer?._id === updatedInventory?._id) setUnitFileViewer(updatedInventory);
    setEditingInventory(null);
  };

  const onDeleteInventory = deletedInventoryId => {
    setContact(current =>
      current
        ? { ...current, inventoryUnit: inventoryUnits(current).filter(unit => unit?._id !== deletedInventoryId) }
        : current
    );
    if (unitFileViewer?._id === deletedInventoryId) setUnitFileViewer(null);
    setEditingInventory(null);
  };

  const openTool = label => {
    if (label === 'Dochub') {
      if (contract?._id) openLinkInNewTab(`/dochub/print-groups?contractId=${contract._id}&source=Contract`);
      else openLinkInNewTab(`/dochub/print-groups?contactId=${lead._id}&source=Contact`);
      return;
    }
    if (!ready()) return;
    if (label === 'File Checklist') setDochubDoc('Customer File Checklist');
    if (label === 'Comm. Sheet') setDochubDoc('Commission Sheet');
    if (label === 'Files') setShowFiles(true);
    if (label === 'P&L') setShowPnl(true);
    if (label === 'Payroll') openPayroll();
    if (label === 'Calculation') {
      const unit = contract?.inventoryUnit?._id ? contract.inventoryUnit : units[0];
      if (!unit?._id) {
        toast.error('This customer has no inventory unit yet.');
        return;
      }
      setCalcUnit(unit);
      setShowCalc(true);
    }
  };

  const removeFile = async () => {
    setDeleteFile({ ...deleteFile, deleting: true });
    const unit = deleteFile.unit;
    if (unit?._id) {
      const files = (unit.files || []).filter(file => file._id !== deleteFile.file._id).map(file => file._id);
      const { response, error } = await makeApiRequests({
        requestBody: { files },
        endpoint: ENDPOINTS.INVENTORY_WITH_ID(unit._id),
        method: 'PUT'
      });
      if (error) {
        setDeleteFile({ ...deleteFile, deleting: false });
        toast.error(error);
        return;
      }
      onUpdateInventory({
        ...unit,
        ...(response || {}),
        files: (unit.files || []).filter(file => file._id !== deleteFile.file._id)
      });
      deleteFileFromServer(deleteFile.file);
      setDeleteFile(null);
      return;
    }

    const files = (contact.files || []).filter(file => file._id !== deleteFile.file._id).map(file => file._id);
    const { response, error } = await makeApiRequests({
      requestBody: { files },
      endpoint: ENDPOINTS.CONTACTS_WITH_ID(contact._id),
      method: 'PUT'
    });
    if (error) {
      setDeleteFile({ ...deleteFile, deleting: false });
      toast.error(error);
      return;
    }
    setContact(response);
    deleteFileFromServer(deleteFile.file);
    setDeleteFile(null);
  };

  return (
    <>
      <div className="today-drawer-backdrop" style={toolOpen ? { zIndex: 900 } : undefined} onMouseDown={onClose} />
      <aside className="today-drawer" style={toolOpen ? { zIndex: 910 } : undefined} role="dialog" aria-label={lead.buyer}>
        <div className="today-drawer-header bg-primary text-white p-4">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <Button variant="link" className="text-white p-0" onClick={onClose}>
              <ArrowLeft className="mr-1" /> Back
            </Button>
            <div className="d-flex align-items-center">
              {(lead.exported || contract) && <span className="today-contracted-pill mr-2">Contracted</span>}
              <Dropdown alignRight className="today-status-dropdown">
                <Dropdown.Toggle
                  variant="light"
                  id="customer-status"
                  className="today-status-toggle border-0"
                  disabled={statusSaving || !statusOptions.length}
                  aria-label="Change status"
                >
                  {currentStatus || 'Status'}
                </Dropdown.Toggle>
                <Dropdown.Menu alignRight className="today-status-menu">
                  {statusOptions.map(status => (
                    <Dropdown.Item key={status} active={status === currentStatus} onClick={() => changeStatus(status)}>
                      {status}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </div>
          <h2 className="h3 font-weight-bold mb-1">{lead.buyer}</h2>
          <div className="small" style={{ opacity: 0.85 }}>
            {subtitle || 'Customer'}
          </div>
        </div>
        <div className="d-flex border-bottom">
          <Button
            as={phone ? 'a' : 'button'}
            href={phone ? `tel:${phone}` : undefined}
            variant="link"
            className="flex-fill text-dark py-3"
            disabled={!phone}
          >
            <Telephone className="d-block mx-auto mb-1" /> Call
          </Button>
          <Button
            as={phone ? 'a' : 'button'}
            href={phone ? `sms:${phone}` : undefined}
            variant="link"
            className="flex-fill text-dark py-3"
            disabled={!phone}
          >
            <ChatLeftText className="d-block mx-auto mb-1" /> Text
          </Button>
          <Button variant="link" className="flex-fill text-dark py-3" onClick={onNote}>
            <JournalText className="d-block mx-auto mb-1" /> Add note
          </Button>
          <Button
            variant="link"
            className="flex-fill text-dark py-3"
            onClick={() => {
              if (!showTasks && !ready()) return;
              setShowTasks(open => !open);
            }}
          >
            <Calendar className="d-block mx-auto mb-1" /> Task
          </Button>
        </div>
        <div className="p-4">
          {showTasks && (
            <div className="mb-4">
              <Tasks
                tasks={contact?.tasks || []}
                appChoices={appChoices}
                currentUserId={currentUserId}
                onTasksSubmit={submitTasks}
              />
            </div>
          )}
          {showNextAction && (
            <section className="border rounded p-3 mb-4" style={{ borderLeft: '4px solid #28a745' }}>
              <div className="text-muted small font-weight-bold text-uppercase">{actionLabel}</div>
              <h3 className="h5 font-weight-bold mt-2 mb-2">{lead.reason}</h3>
              <p className="text-muted small mb-3">Last contact: {lastContact}</p>
              <div className="d-flex">
                <Button variant="success" className="flex-fill mr-2" onClick={onComplete}>
                  Complete action
                </Button>
                <Button variant="outline-dark" className="flex-fill" onClick={onReschedule}>
                  Reschedule
                </Button>
              </div>
            </section>
          )}
          <section className="mb-4">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h3 className="h6 font-weight-bold mb-0">Deal tools</h3>
              <span className="text-muted small">Most-used first</span>
            </div>
            <Row>
              {dealTools.map(([Icon, label, variant]) => (
                <Col key={label} xs={3} className="mb-2 px-1">
                  {label === 'Copy' ? (
                    <div className="dropdown">
                      <Button variant={variant} className="w-100 py-3" onClick={() => setCopyOpen(open => !open)}>
                        <Icon className="d-block mx-auto mb-1" />
                        <span className="small">Copy</span>
                      </Button>
                      <div className={`dropdown-menu ${copyOpen ? 'show' : ''}`}>
                        <button
                          type="button"
                          className="dropdown-item"
                          onClick={() => {
                            setCopyOpen(false);
                            if (!ready()) return;
                            copyContactDetails(contact, units[0] || {});
                            toast.success('Copied Contact!');
                          }}
                        >
                          Contact
                        </button>
                        <button
                          type="button"
                          className="dropdown-item"
                          onClick={() => {
                            setCopyOpen(false);
                            if (!ready()) return;
                            copyContactAddress(contact);
                            toast.success('Copied Address!');
                          }}
                        >
                          Address
                        </button>
                      </div>
                    </div>
                  ) : (
                    <Button variant={variant} className="w-100 py-3" onClick={() => openTool(label)}>
                      <Icon className="d-block mx-auto mb-1" />
                      <span className="small">{label}</span>
                    </Button>
                  )}
                </Col>
              ))}
            </Row>
          </section>
          <section>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h3 className="h6 font-weight-bold mb-0">Customer snapshot</h3>
              <Button variant="link" className="p-0" onClick={onOpenRecord}>
                View full record
              </Button>
            </div>
            <div className="border rounded">
              <div className="d-flex justify-content-between border-bottom p-3 small">
                <span className="text-muted">Unit</span>
                <div className="text-right ml-3">
                  {contact ? (
                    <div className="d-flex flex-column align-items-end">
                      {units.map((unit, index) => (
                        <div
                          key={unit._id || index}
                          className={`d-flex align-items-center justify-content-end ${index > 0 ? 'mt-1' : ''}`}
                          style={{ gap: '0.5rem' }}
                        >
                          <span className="small">{getModelLabel(unit?.model)}</span>
                          <span className="d-inline-flex align-items-center" style={{ gap: '0.5rem' }}>
                            <Files
                              size={14}
                              style={{ cursor: 'pointer' }}
                              onClick={() => setUnitFileViewer(unit)}
                            />
                            <Pencil
                              size={14}
                              style={{ cursor: 'pointer' }}
                              onClick={() => setEditingInventory(unit)}
                            />
                            <ArrowDownUp
                              size={14}
                              style={{ cursor: 'pointer' }}
                              onClick={() => setInventoryPopup({ replaceId: unit?._id })}
                            />
                            <Trash
                              size={14}
                              className="text-danger"
                              style={{ cursor: 'pointer' }}
                              onClick={() => setRemoveUnitId(unit?._id)}
                            />
                          </span>
                        </div>
                      ))}
                      <UnderlineButton
                        variant="success"
                        Icon={() => <Plus size={14} />}
                        text="Link New"
                        fontSize="small"
                        className={units.length ? 'mt-1' : undefined}
                        disabled={savingUnits}
                        onClick={() => setInventoryPopup({})}
                      />
                    </div>
                  ) : (
                    '…'
                  )}
                </div>
              </div>
              <div className="d-flex justify-content-between border-bottom p-3 small">
                <span className="text-muted">Property address</span>
                <strong className="text-right ml-3">{contact ? address || 'Not on file' : '…'}</strong>
              </div>
              <div className="d-flex justify-content-between align-items-center border-bottom p-3 small">
                <span className="text-muted">Commission</span>
                <Button
                  variant="link"
                  className="p-0 font-weight-bold text-right ml-3"
                  onClick={() => {
                    if (!ready()) return;
                    setDochubDoc('Commission Sheet');
                  }}
                >
                  {commissionDue == null ? 'Commission sheet' : money(commissionDue)}
                  <Pencil className="ml-1" size={12} />
                </Button>
              </div>
              <div className="d-flex justify-content-between p-3 small">
                <span className="text-muted">Last contact</span>
                <strong className="text-right ml-3">{lastContact}</strong>
              </div>
            </div>
          </section>
        </div>
      </aside>
      <DochubSidebar
        show={Boolean(dochubDoc)}
        onHide={() => setDochubDoc(null)}
        appChoices={appChoices}
        source={contract?._id ? 'Contract' : 'Contact'}
        contactId={contract?._id ? undefined : lead._id}
        contractId={contract?._id}
        document={dochubDoc || 'Commission Sheet'}
      />
      <FileViewer
        sidebarZIndex={1200}
        show={showFiles}
        onHide={() => setShowFiles(false)}
        files={contact?.files}
        driveFolder={contact?.driveFolder}
        onFileDeleteClick={file => setDeleteFile({ file })}
      />
      <FileViewer
        sidebarZIndex={1200}
        show={Boolean(unitFileViewer)}
        onHide={() => setUnitFileViewer(null)}
        files={unitFileViewer?.files}
        onFileDeleteClick={file => setDeleteFile({ file, unit: unitFileViewer })}
      />
      <AlertModal
        show={deleteFile !== null}
        alertText="Are you sure you want to delete this file? You will lose access to it permanently."
        showProgress={deleteFile?.deleting}
        progressText="Deleting file..."
        continueButtonText="Delete"
        onDismissClick={() => setDeleteFile(null)}
        onHide={() => setDeleteFile(null)}
        onContinueClick={removeFile}
      />
      {contact && (
        <PnLSidebar
          visible={showPnl}
          onClose={() => setShowPnl(false)}
          contract={contract ? { ...contract, newStatus: 'Contracted' } : { ...contact, newStatus: 'Working' }}
          appChoices={appChoices}
        />
      )}
      <PayrollViewSidebar
        show={showPayroll}
        activeContract={contract}
        onHide={() => setShowPayroll(false)}
        payrollBillMeta={payrollBillMeta}
        onPayrollBillMetaChange={setPayrollBillMeta}
        submitPayroll={submitPayroll}
        updatePayroll={updatePayroll}
        payrolls={payrolls}
        onPayrollsChange={setPayrolls}
        loading={payrollLoading}
        setToBeDeletedBill={setToBeDeletedBill}
        deleteItem={deletePayroll}
        deleteInProgress={deleteInProgress}
        toBeDeletedBill={toBeDeletedBill}
        onPayrollAdd={payroll => setPayrolls(current => [payroll, ...current])}
        onShowRecordPayrollSidebarChange={setShowRecordPayroll}
        showRecordPayrollSidebar={showRecordPayroll}
        onInternalPayrollFileDeletion={(payrollId, fileId) =>
          setPayrolls(current =>
            current.map(item =>
              item._id === payrollId
                ? { ...item, files: (item.files || []).filter(file => file._id !== fileId) }
                : item
            )
          )
        }
      />
      <CalculationWorksheetSidebar
        visible={showCalc}
        onClose={() => setShowCalc(false)}
        inventory={calcUnit}
        contact={contact}
        appChoices={appChoices}
        inventoryUnitOptions={units}
        selectedInventoryId={calcUnit?._id}
        onInventoryUnitChange={setCalcUnit}
        onContactUpdate={setContact}
      />
      <InventoryEditSideBar
        appChoices={appChoices}
        onHide={() => setEditingInventory(null)}
        editingInventory={editingInventory}
        onInventoryEdit={onUpdateInventory}
        onInventoryDelete={onDeleteInventory}
        zIndex={1100}
      />
      <InventoryPicker
        show={inventoryPopup !== null}
        onSubmit={onInventorySubmit}
        selectedUnits={inventoryPopup?.selected ? [inventoryPopup.selected] : []}
        onUnitSelect={unit => setInventoryPopup(prev => ({ ...prev, selected: unit }))}
        onInventoryPickerClose={() => setInventoryPopup(null)}
        excludedUnitIds={unitIds}
        submitting={savingUnits}
      />
      <AlertModal
        show={removeUnitId != null}
        alertText="Are you sure to remove this inventory unit from contact?"
        showProgress={savingUnits}
        progressText="Removing unit..."
        onDismissClick={() => setRemoveUnitId(null)}
        onHide={() => setRemoveUnitId(null)}
        onContinueClick={() => saveContactUnits(unitIds.filter(id => id !== removeUnitId))}
      />
    </>
  );
}

export default CustomerDrawer;
